import React from 'react';
import { motion } from 'framer-motion';
import { Home, Star, MapPin, Grid, Sun, Moon } from 'lucide-react';
import { NavigationMode } from '../../types';
import { useTheme } from '../styles/ThemeContext';
interface ModeSelectorProps {
  activeMode: NavigationMode;
  onSelectMode: (mode: NavigationMode) => void;
}
export function ModeSelector({
  activeMode,
  onSelectMode
}: ModeSelectorProps) {
  const {
    isDark,
    toggleTheme
  } = useTheme();
  const modes: {
    id: NavigationMode;
    label: string;
    icon: React.ElementType;
  }[] = [{
    id: 'home',
    label: 'Home',
    icon: Home
  }, {
    id: 'amenities',
    label: 'Amenities',
    icon: Star
  }, {
    id: 'surroundings',
    label: 'Nearby',
    icon: MapPin
  }, {
    id: 'lots',
    label: 'Lots',
    icon: Grid
  }];
  return <div className={`fixed left-0 top-0 bottom-0 w-[100px] backdrop-blur-xl z-50 flex flex-col items-center py-8 shadow-2xl transition-all duration-300 ${isDark ? 'bg-slate-900/70 border-r border-white/10' : 'bg-white/70 border-r border-white/40 shadow-xl'}`} data-id="element-801">
      <div className="flex flex-col gap-6 w-full" data-id="element-802">
        {modes.map(mode => {
        const isActive = activeMode === mode.id;
        const Icon = mode.icon;
        return <button key={mode.id} onClick={() => onSelectMode(mode.id)} className="relative group w-full flex flex-col items-center justify-center py-3 gap-1.5 transition-all outline-none" aria-label={`Switch to ${mode.label} mode`} data-id="element-803">
              {/* Active Indicator Border */}
              {isActive && <motion.div layoutId="activeModeBorder" className={`absolute left-0 top-0 bottom-0 w-1 ${isDark ? 'bg-cyan-500' : 'bg-teal-600'}`} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.3
          }} data-id="element-804" />}

              {/* Icon Container */}
              <div className={`
                  p-3 rounded-xl transition-all duration-300
                  ${isActive ? isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-teal-600/15 text-teal-600' : isDark ? 'text-slate-400 group-hover:text-white group-hover:bg-white/5' : 'text-gray-500 group-hover:text-gray-900 group-hover:bg-white/50'}
                `} data-id="element-805">
                <Icon className={`w-6 h-6 ${isActive ? 'stroke-[2.5px]' : 'stroke-2'}`} data-id="element-806" />
              </div>

              {/* Label */}
              <span className={`
                  text-[10px] font-medium tracking-wider uppercase transition-colors duration-300 text-center px-2
                  ${isActive ? isDark ? 'text-cyan-400' : 'text-teal-600' : isDark ? 'text-slate-500 group-hover:text-slate-300' : 'text-gray-500 group-hover:text-gray-700'}
                `} data-id="element-807">
                {mode.label}
              </span>
            </button>;
      })}
      </div>

      {/* Theme Toggle - Bottom */}
      <div className="mt-auto pb-6" data-id="element-808">
        <button onClick={toggleTheme} className={`p-3 rounded-xl transition-all duration-300 ${isDark ? 'text-slate-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-white/50'}`} aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`} data-id="element-809">
          {isDark ? <Sun className="w-5 h-5" data-id="element-810" /> : <Moon className="w-5 h-5" data-id="element-811" />}
        </button>
      </div>
    </div>;
}